import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { 
  Mail, 
  ArrowLeft, 
  AlertCircle,
  CheckCircle,
  KeyRound
} from 'lucide-react';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const { resetPassword } = useAuth();
  const { theme } = useTheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      await resetPassword(email);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 ${
      theme === 'dark' 
        ? 'bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900' 
        : 'bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100'
    }`}>
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className={`absolute -top-40 -right-32 w-80 h-80 rounded-full blur-3xl ${
          theme === 'dark'
            ? 'bg-gradient-to-r from-blue-600 to-purple-600 opacity-30'
            : 'bg-gradient-to-r from-blue-400 to-purple-500 opacity-20'
        }`}></div>
        <div className={`absolute -bottom-40 -left-32 w-80 h-80 rounded-full blur-3xl ${
          theme === 'dark'
            ? 'bg-gradient-to-r from-green-600 to-blue-600 opacity-30'
            : 'bg-gradient-to-r from-green-400 to-blue-500 opacity-20'
        }`}></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Back to Login link */}
        <div className="mb-6">
          <Link
            to="/login"
            className={`inline-flex items-center space-x-2 text-sm font-medium transition-colors ${
              theme === 'dark'
                ? 'text-blue-400 hover:text-blue-300'
                : 'text-blue-600 hover:text-blue-500'
            }`}
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to login</span>
          </Link>
        </div>

        <Card className={`backdrop-blur-sm shadow-xl ${
          theme === 'dark'
            ? 'bg-white/10 border-white/20'
            : 'bg-white/80 border-white/20'
        }`}>
          <CardHeader className="space-y-1 text-center pb-8">
            <div className="mx-auto w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg mb-4">
              <KeyRound className="w-8 h-8 text-white" />
            </div>
            <CardTitle className={`text-2xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Reset your password
            </CardTitle>
            <CardDescription className={`${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {success 
                ? "Check your email for reset instructions"
                : "Enter your email address and we'll send you a password reset link"
              }
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {success ? (
              <div className="space-y-6">
                <div className={`flex items-start space-x-3 p-4 rounded-lg ${
                  theme === 'dark'
                    ? 'bg-green-900/50 border border-green-800'
                    : 'bg-green-50 border border-green-200'
                }`}>
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-green-300' : 'text-green-800'
                    }`}>
                      Password reset email sent!
                    </p>
                    <p className={`text-sm ${
                      theme === 'dark' ? 'text-green-400' : 'text-green-700'
                    }`}>
                      We've sent a password reset link to <strong>{email}</strong>. 
                      Please check your inbox and follow the instructions to reset your password.
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <p className={`text-sm text-center ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    Didn't receive the email?
                  </p>
                  <ul className={`text-xs space-y-1 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    <li>• Check your spam or junk folder</li>
                    <li>• Make sure you entered the correct email address</li>
                    <li>• Wait a few minutes for the email to arrive</li>
                  </ul>
                  <Button
                    onClick={() => {
                      setSuccess(false);
                      setEmail('');
                    }}
                    variant="outline"
                    className="w-full"
                  >
                    Try a different email
                  </Button>
                </div>

                <div className={`text-center pt-4 border-t ${
                  theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                  <Link
                    to="/login"
                    className={`text-sm font-medium transition-colors ${
                      theme === 'dark'
                        ? 'text-blue-400 hover:text-blue-300'
                        : 'text-blue-600 hover:text-blue-500'
                    }`}
                  >
                    Return to login
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className={`flex items-center space-x-2 p-4 rounded-lg ${
                    theme === 'dark'
                      ? 'bg-red-900/50 border border-red-800'
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <p className={`text-sm ${
                      theme === 'dark' ? 'text-red-300' : 'text-red-700'
                    }`}>{error}</p>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email" className={`text-sm font-medium ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Email address
                  </Label>
                  <div className="relative">
                    <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                    }`} />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="pl-10 h-12"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium rounded-lg shadow-sm transition-all duration-200 hover:shadow-md"
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <LoadingSpinner size="sm" />
                      <span>Sending reset link...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Mail className="w-5 h-5" />
                      <span>Send reset link</span>
                    </div>
                  )}
                </Button>
                
                <div className={`text-center pt-4 border-t ${
                  theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                  <p className={`text-sm ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    Remember your password?{' '}
                    <Link
                      to="/login"
                      className={`font-medium transition-colors ${
                        theme === 'dark'
                          ? 'text-blue-400 hover:text-blue-300'
                          : 'text-blue-600 hover:text-blue-500'
                      }`}
                    >
                      Sign in
                    </Link>
                  </p>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Security note */}
        <div className={`mt-6 text-center text-xs ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
        }`}>
          <p>
            For security reasons, we'll send the reset link to the email address 
            associated with your account. The link will expire in 1 hour.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;


